/**
 * The dashboard's Performance section: how the ads went over the last 7–90
 * days of the ad account's calendar, for every campaign or one. It shows the
 * spend and Google's clicks, the visits our landing pages recorded, the guest
 * trials and the accounts created, by campaign and day, then the keywords and
 * search terms that brought visitors, the landing pages, and a tracking check
 * comparing Google's click log with ours.
 *
 * One server read (`GET …/dashboard/performance`), the same report Fias AI's
 * `adopt_get_performance_report` reads, so the two never disagree. Search
 * terms and landing paths are typed by outsiders. They render as text only.
 */

import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { AdoptPerformanceReportDto } from '@/lib/api';
import { useAdoptCampaigns, useAdoptPerformanceReport } from '../hooks/useAdvertisingOptimization';
import { ARCHE_PATH } from '../constants';

const WINDOWS = [7, 14, 30, 90] as const;

function usd(cents: number | null): string {
  if (cents === null) return '—';
  return `$${(cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const n = (value: number): string => value.toLocaleString();

/** "$7.28 per trial", or nothing when there is nothing to divide by. */
const perNote = (cents: number | null, unit: string): string | undefined =>
  cents === null ? undefined : `${usd(cents)} per ${unit}`;

/** "Oct 28, 3:30 PM" for an instant, in the viewer's time zone. */
function timeLabel(at: string): string {
  return new Date(at).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** "Oct 24" for a YYYY-MM-DD day (a calendar day, so read in UTC). */
function dayLabel(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function Tile({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="rounded-lg border border-border p-4">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold">{value}</div>
      {note && <div className="text-xs text-muted-foreground mt-0.5">{note}</div>}
    </div>
  );
}

function ReportTable({
  title,
  note,
  head,
  children,
  empty,
}: {
  title: string;
  note?: ReactNode;
  head: string[];
  children: ReactNode;
  empty: boolean;
}) {
  return (
    <section className="rounded-lg border border-border">
      <div className="px-4 pt-3 pb-2">
        <h3 className="font-medium">{title}</h3>
        {note && <p className="text-xs text-muted-foreground mt-0.5">{note}</p>}
      </div>
      {empty ? (
        <p className="px-4 pb-4 text-sm text-muted-foreground">Nothing in this window yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-y border-border text-left text-muted-foreground">
                {head.map((h, i) => (
                  <th
                    key={h}
                    className={`px-4 py-2 font-medium whitespace-nowrap ${i > 0 ? 'text-right' : ''}`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">{children}</tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** A table row: the first cell is a label, the rest are right-aligned figures. */
function Row({ cells }: { cells: ReactNode[] }) {
  return (
    <tr>
      {cells.map((c, i) => (
        <td key={i} className={`px-4 py-2 ${i > 0 ? 'text-right tabular-nums' : ''}`}>
          {c}
        </td>
      ))}
    </tr>
  );
}

function TrackingCheck({ tracking }: { tracking: AdoptPerformanceReportDto['tracking'] }) {
  const { googleClickLog, visitsMatched, beaconRecorded, visitsNotInGoogle } = tracking;
  const lines: string[] = [];
  if (googleClickLog === 0) {
    lines.push("Google's click log has no clicks for this window yet.");
  } else {
    lines.push(
      `Google logged ${n(googleClickLog)} click${googleClickLog === 1 ? '' : 's'}. ` +
        `Our landing pages recorded ${n(visitsMatched)} of them, and Google's tracking ` +
        `beacon recorded ${n(beaconRecorded)}.`,
    );
    const missed = googleClickLog - visitsMatched;
    if (missed > 0) {
      lines.push(
        `${n(missed)} click${missed === 1 ? '' : 's'} left no visit on our side: the visitor ` +
          'may have left before the page loaded, or declined optional cookies.',
      );
    }
  }
  if (visitsNotInGoogle > 0) {
    lines.push(
      `${n(visitsNotInGoogle)} visit${visitsNotInGoogle === 1 ? '' : 's'} carried a Google ` +
        "click id that Google's click log does not have.",
    );
  }
  return (
    <section className="rounded-lg border border-border p-4">
      <h3 className="font-medium">Tracking check</h3>
      <ul className="mt-1 text-sm text-muted-foreground list-disc pl-5 space-y-0.5">
        {lines.map((l) => (
          <li key={l}>{l}</li>
        ))}
      </ul>
    </section>
  );
}

interface Props {
  holderId: string;
}

export function PerformanceReport({ holderId }: Props) {
  const [days, setDays] = useState<number>(7);
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const campaignsQuery = useAdoptCampaigns(holderId);
  const reportQuery = useAdoptPerformanceReport(holderId, days, campaignId);
  const report = reportQuery.data;

  const controls = (
    <div className="flex flex-wrap items-center gap-2">
      <select
        aria-label="Campaign"
        className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
        value={campaignId ?? ''}
        onChange={(e) => setCampaignId(e.target.value || null)}
      >
        <option value="">All campaigns</option>
        {(campaignsQuery.data ?? []).map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
      <div className="flex rounded-md border border-border overflow-hidden" role="group">
        {WINDOWS.map((w) => (
          <button
            key={w}
            type="button"
            aria-pressed={days === w}
            onClick={() => setDays(w)}
            className={`px-2.5 py-1.5 text-sm ${
              days === w ? 'bg-primary text-primary-foreground' : 'hover:bg-muted/50'
            }`}
          >
            {w}d
          </button>
        ))}
      </div>
    </div>
  );

  let body: ReactNode;
  if (reportQuery.isLoading) {
    body = <div className="text-muted-foreground">Loading…</div>;
  } else if (reportQuery.isError || !report) {
    body = <div className="text-sm text-destructive">The report could not be loaded.</div>;
  } else {
    const t = report.totals;
    const terms = report.searchTerms;
    body = (
      <div className="flex flex-col gap-4">
        <p className="text-xs text-muted-foreground">
          {dayLabel(report.window.from)} – {dayLabel(report.window.to)}, the ad account's days (
          {report.window.timeZone}). Google reports a day's clicks a few hours late, so today fills
          in as it goes.
          {report.asOf.googleSyncedAt && (
            <> Google's numbers as of {timeLabel(report.asOf.googleSyncedAt)}.</>
          )}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Tile label="Spend" value={usd(t.costCents)} />
          <Tile label="Impressions" value={n(t.impressions)} />
          <Tile
            label="Clicks"
            value={n(t.googleClicks)}
            note={perNote(t.costPerClickCents, 'click')}
          />
          <Tile label="Visits" value={n(t.visits)} note="recorded by our landing pages" />
          <Tile label="Trials" value={n(t.trials)} note={perNote(t.costPerTrialCents, 'trial')} />
          <Tile
            label="Accounts"
            value={n(t.accounts)}
            note={perNote(t.costPerAccountCents, 'account')}
          />
        </div>

        <ReportTable
          title="Campaigns"
          head={['Campaign', 'Spend', 'Clicks', 'Visits', 'Trials', 'Accounts', 'Per trial']}
          empty={report.campaigns.length === 0}
        >
          {report.campaigns.map((c) => (
            <Row
              key={c.campaignId ?? 'none'}
              cells={[
                c.campaignId ? (
                  <Link
                    to={`${ARCHE_PATH}/campaigns/${c.campaignId}`}
                    className="font-medium hover:underline"
                  >
                    {c.name}
                  </Link>
                ) : (
                  <span className="text-muted-foreground">{c.name}</span>
                ),
                usd(c.costCents),
                n(c.googleClicks),
                n(c.visits),
                n(c.trials),
                n(c.accounts),
                usd(c.costPerTrialCents),
              ]}
            />
          ))}
        </ReportTable>

        <ReportTable
          title="By day"
          head={['Day', 'Spend', 'Impressions', 'Clicks', 'Visits', 'Trials', 'Accounts']}
          empty={false}
        >
          {[...report.days].reverse().map((d) => (
            <Row
              key={d.day}
              cells={[
                <span className="whitespace-nowrap">{dayLabel(d.day)}</span>,
                usd(d.costCents),
                n(d.impressions),
                n(d.googleClicks),
                n(d.visits),
                n(d.trials),
                n(d.accounts),
              ]}
            />
          ))}
        </ReportTable>

        <ReportTable
          title="Keywords"
          note="Each click in Google's click log, followed into our visits and trials. Accounts here are trials that upgraded."
          head={['Keyword', 'Clicks', 'Visits', 'Trials', 'Accounts']}
          empty={report.keywords.length === 0}
        >
          {report.keywords.map((k) => (
            <Row
              key={`${k.keyword}|${k.matchType}`}
              cells={[
                <>
                  {k.keyword || <span className="text-muted-foreground">(no keyword)</span>}
                  {k.matchType && (
                    <span className="ml-1.5 text-xs text-muted-foreground lowercase">
                      {k.matchType}
                    </span>
                  )}
                </>,
                n(k.clicks),
                n(k.visits),
                n(k.trials),
                n(k.accounts),
              ]}
            />
          ))}
        </ReportTable>

        <ReportTable
          title="What people searched"
          note={
            terms.totals.rows === 0
              ? undefined
              : `Google shows the searches behind ${n(terms.totals.clicks)} of ${n(
                  t.googleClicks,
                )} clicks (it hides rare ones). ${n(terms.totals.rows)} rows, ${usd(
                  terms.totals.costCents,
                )}${terms.rows.length < terms.totals.rows ? `; the top ${n(terms.rows.length)} are shown` : ''}.`
          }
          head={['Search (keyword)', 'Impressions', 'Clicks', 'Cost']}
          empty={terms.rows.length === 0}
        >
          {terms.rows.map((r) => (
            <Row
              key={`${r.searchTerm}|${r.keyword}|${r.matchType}`}
              cells={[
                <>
                  <div>{r.searchTerm}</div>
                  <div className="text-xs text-muted-foreground">{r.keyword}</div>
                </>,
                n(r.impressions),
                n(r.clicks),
                usd(r.costCents),
              ]}
            />
          ))}
        </ReportTable>

        <ReportTable
          title="Landing pages"
          head={['Page', 'Visits', 'Trials', 'Accounts']}
          empty={report.landingPages.length === 0}
        >
          {report.landingPages.map((p) => (
            <Row
              key={p.path}
              cells={[
                <span className="break-all">{p.path || '(unknown)'}</span>,
                n(p.visits),
                n(p.trials),
                n(p.accounts),
              ]}
            />
          ))}
        </ReportTable>

        <TrackingCheck tracking={report.tracking} />
      </div>
    );
  }

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-medium">Performance</h2>
        {controls}
      </div>
      {body}
    </section>
  );
}
